import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add token to requests if it exists
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['x-auth-token'] = token;
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// ==================== AUTH APIS ====================
export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    getCurrentUser: () => api.get('/auth/me'),
    changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
};

// ==================== DESTINATION APIS ====================
export const destinationAPI = {
    getAllDestinations: (params) => api.get('/destinations', { params }),
    getDestinationById: (id) => api.get(`/destinations/${id}`),
    getDestinationStats: () => api.get('/destinations/stats'),
    searchDestinations: (searchTerm) => api.get('/destinations', { params: { search: searchTerm } }),
    getDestinationsByRegion: (region) => api.get('/destinations', { params: { region } }),
    getFeaturedDestinations: (limit = 3) => api.get('/destinations', { params: { featured: limit } })
};

// Regions API
export const regionAPI = {
    getAllRegions: () => api.get('/regions'),
    getRegionList: () => api.get('/regions/list')
};

// ==================== PACKAGE APIS ====================
export const packageAPI = {
    getAllPackages: (params) => api.get('/packages', { params }),
    getPackageById: (id) => api.get(`/packages/${id}`),
    getTrendingPackages: () => api.get('/packages/trending'),
    getPackagesByType: (type) => api.get('/packages', { params: { type } }),
    searchPackages: (searchTerm) => api.get('/packages', { params: { search: searchTerm } }),
    getPackageStats: () => api.get('/packages/stats')
};

export const addonServiceAPI = {
    getApplicableForPackage: (packageId) => api.get(`/addon-services/package/${packageId}`),
    getAdminList: (params) => api.get('/addon-services/admin/all', { params }),
    getById: (id) => api.get(`/addon-services/admin/${id}`)
};

// ==================== BOOKING APIS ====================
export const bookingAPI = {
    createBooking: (bookingData) => api.post('/bookings', bookingData),
    getBookingById: (id) => api.get(`/bookings/${id}`),
    trackBooking: (bookingId) => api.get(`/bookings/track/${bookingId}`),
    getCustomerBookings: (email) => api.get(`/bookings/customer/${email}`),
    cancelBooking: (id, email) => api.post(`/bookings/${id}/cancel`, { email })
};

export const couponAPI = {
    validateCoupon: (code, bookingAmount) => api.post('/coupons/validate', {
        code,
        booking_amount: bookingAmount
    }),
    getAllCoupons: (params) => api.get('/coupons', { params }),
    createCoupon: (payload) => api.post('/coupons', payload),
    updateCoupon: (id, payload) => api.put(`/coupons/${id}`, payload),
    deleteCoupon: (id) => api.delete(`/coupons/${id}`)
};

export const reviewAPI = {
    getSummary: (entityType, ids) => api.get('/reviews/summary', {
        params: {
            entity_type: entityType,
            ids: Array.isArray(ids) ? ids.join(',') : ids
        }
    }),
    getReviews: (entityType, entityId) => api.get(`/reviews/${entityType}/${entityId}`),
    createReview: (payload) => api.post('/reviews', payload),
    getAllReviews: (params) => api.get('/reviews', { params }),
    updateReviewStatus: (id, status) => api.patch(`/reviews/${id}/status`, { status }),
    deleteReview: (id) => api.delete(`/reviews/${id}`)
};

// ==================== CONTACT APIS ====================
export const contactAPI = {
    submitContact: (contactData) => api.post('/contact/contact', contactData),
    subscribeNewsletter: (email) => api.post('/contact/newsletter/subscribe', { email }),
    unsubscribeNewsletter: (email) => api.post('/contact/newsletter/unsubscribe', { email })
};

export default api;
